// Small formatting and validation helpers shared by the server and the screens.

/** US phone numbers are stored as 10 digits. Returns null when it isn't a phone number. */
export function normalizePhone(input: string | null | undefined): string | null {
  if (!input) return null;
  let d = String(input).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d.length === 10 ? d : null;
}

export function formatPhone(digits: string | null | undefined): string {
  if (!digits) return '';
  const d = digits.replace(/\D/g, '');
  if (d.length !== 10) return digits;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** IMEI numbers are 15 digits with a Luhn check digit. */
export function isValidImei(input: string | null | undefined): boolean {
  if (!input) return false;
  const d = input.replace(/\D/g, '');
  if (d.length !== 15) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let n = Number(d[i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
}

export function cleanImei(input: string | null | undefined): string | null {
  if (!input) return null;
  const d = input.replace(/\D/g, '');
  return d.length ? d : null;
}

export function fullName(p: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
  if (!p) return '';
  return [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').toUpperCase() + (parts.length > 1 ? parts[parts.length - 1][0].toUpperCase() : '');
}

/** Phone grades, best first: the same scale for phones we buy and phones we sell. */
export const GRADES = ['A', 'B+', 'B', 'B-', 'C', 'D', 'parts'] as const;
export type Grade = (typeof GRADES)[number];
export const GRADE_LABELS: Record<string, string> = {
  A: 'Grade A', 'B+': 'Grade B+', B: 'Grade B', 'B-': 'Grade B-', C: 'Grade C', D: 'Grade D', parts: 'Parts only',
};

/**
 * Grades for buying and trade-ins, from the customer buying list. "New/Sealed" is a phone that was
 * never activated; the rest are used.
 */
export const BUY_GRADES = ['New/Sealed', 'A', 'B+', 'B', 'B-', 'C', 'D'] as const;
export const BUY_GRADE_HELP: Record<string, string> = {
  'New/Sealed': 'Sealed in the box, never activated.',
  A: 'Like new: no scratches, scuffs or dents. Battery 100%.',
  'B+': 'Like new with light scratches. Battery 90% or more.',
  B: 'Works perfectly. Scratches, no dents. Battery 80% or more.',
  'B-': 'Works perfectly. Heavier scratches or small dents, or battery under 80%.',
  C: 'Works. Cracked glass, dents or deep scratches.',
  D: 'Bad screen or touch, screen not original, or bent.',
};

/** The inventory condition and grade for a phone bought in this grade (older names too). */
export function gradeFromBuy(buyGrade: string | null | undefined): { condition: 'new' | 'used'; grade: Grade | null } {
  const c = String(buyGrade ?? '').trim();
  if (/sealed|^new\b/i.test(c)) return { condition: 'new', grade: null };
  if ((GRADES as readonly string[]).includes(c)) return { condition: 'used', grade: c as Grade };
  const older: Record<string, Grade> = { excellent: 'A', good: 'B', fair: 'C', damaged: 'D' };
  return { condition: 'used', grade: older[c.toLowerCase()] ?? null };
}

export const CONDITIONS = ['new', 'open_box', 'used'] as const;
export const CONDITION_LABELS: Record<string, string> = { new: 'New', open_box: 'Open box', used: 'Pre-owned' };

export const DEVICE_STATUS_LABELS: Record<string, string> = {
  in_stock: 'In stock',
  on_hold: 'On hold',
  testing: 'Needs testing',
  in_transit: 'In transit',
  sold: 'Sold',
  parts_only: 'Parts only',
  removed: 'Removed',
};

export const CARRIERS = ['Unlocked', 'AT&T', 'T-Mobile', 'Verizon', 'Cricket', 'Metro by T-Mobile', 'Boost Mobile', 'Simple Mobile', 'Other'] as const;

/** Store-local date key (YYYY-MM-DD) for a timestamp, in the stores' time zone. */
/**
 * The time zone dates and times show in when a caller doesn't pass one: the store's. The web app
 * sets it from the store's settings when someone signs in. The server passes each business's own
 * zone wherever it matters (every business has its own), so this stays Pacific there.
 */
export const zone = { current: 'America/Los_Angeles' };

export function setDisplayTimeZone(tz: string | null | undefined): void {
  if (!tz) return;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    zone.current = tz;
  } catch {
    // not a time zone this browser knows: keep the last good one
  }
}

export function dayKey(ms: number, timeZone = zone.current): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(ms));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function formatDateTime(ms: number, timeZone = zone.current): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(ms));
}

export function formatTime(ms: number, timeZone = zone.current): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(ms));
}
